import React from 'react';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
export function JobProfit() {
  const jobData = [{
    id: 'job-1',
    name: 'GarrettAuto.com Website',
    customer: 'Garrett Auto',
    revenue: 1500,
    costs: {
      labor: 800,
      materials: 200,
      overhead: 150
    },
    status: 'In Progress'
  }, {
    id: 'job-2',
    name: 'SamsDisposal.com Redesign',
    customer: 'Sams Disposal',
    revenue: 5000,
    costs: {
      labor: 2200,
      materials: 500,
      overhead: 400
    },
    status: 'In Progress'
  }, {
    id: 'job-3',
    name: 'DesertSageSeminars.com',
    customer: 'Desert Sage Seminars',
    revenue: 8200,
    costs: {
      labor: 3500,
      materials: 800,
      overhead: 600
    },
    status: 'In Progress'
  }, {
    id: 'job-4',
    name: 'Echo AWS Infrastructure',
    customer: 'Echo',
    revenue: 50000,
    costs: {
      labor: 18000,
      materials: 12000,
      overhead: 3000
    },
    status: 'In Progress'
  }, {
    id: 'job-5',
    name: 'LuckyDuckBath.com',
    customer: 'Lucky Duck Bath',
    revenue: 2500,
    costs: {
      labor: 1100,
      materials: 300,
      overhead: 200
    },
    status: 'In Progress'
  }, {
    id: 'job-6',
    name: 'TeamNTA.com',
    customer: 'Team NTA',
    revenue: 2750,
    costs: {
      labor: 1200,
      materials: 350,
      overhead: 220
    },
    status: 'In Progress'
  }];
  const jobsWithProfit = jobData.map(job => {
    const totalCosts = job.costs.labor + job.costs.materials + job.costs.overhead;
    const profit = job.revenue - totalCosts;
    const margin = profit / job.revenue * 100;
    return {
      ...job,
      totalCosts,
      profit,
      margin
    };
  });
  const totalRevenue = jobsWithProfit.reduce((sum, job) => sum + job.revenue, 0);
  const totalCosts = jobsWithProfit.reduce((sum, job) => sum + job.totalCosts, 0);
  const totalProfit = totalRevenue - totalCosts;
  const averageMargin = totalProfit / totalRevenue * 100;
  const mostProfitableJob = jobsWithProfit.reduce((max, job) => job.profit > max.profit ? job : max);
  const highestMarginJob = jobsWithProfit.reduce((max, job) => job.margin > max.margin ? job : max);
  return <div className="space-y-6" data-id="element-1404">
      <div data-id="element-1405">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1406">Job Profit</h1>
        <p className="text-sm text-gray-600" data-id="element-1407">
          Track profitability by job or project
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1408">
        <Card data-id="element-1409">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1410">Total Revenue</p>
          <p className="text-2xl font-bold text-blue-600" data-id="element-1411">
            {formatCurrency(totalRevenue)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1412">
            {jobsWithProfit.length} active jobs
          </p>
        </Card>

        <Card data-id="element-1413">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1414">Total Costs</p>
          <p className="text-2xl font-bold text-red-600" data-id="element-1415">
            {formatCurrency(totalCosts)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1416">All jobs combined</p>
        </Card>

        <Card data-id="element-1417">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1418">Total Profit</p>
          <p className="text-2xl font-bold text-green-600" data-id="element-1419">
            {formatCurrency(totalProfit)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1420">
            {averageMargin.toFixed(1)}% avg margin
          </p>
        </Card>

        <Card data-id="element-1421">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1422">Best Margin</p>
          <p className="text-2xl font-bold text-purple-600" data-id="element-1423">
            {highestMarginJob.margin.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1424">{highestMarginJob.name}</p>
        </Card>
      </div>

      {/* Jobs Table */}
      <Card data-id="element-1425">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1426">
          Job Profitability
        </h2>
        <div className="overflow-x-auto" data-id="element-1427">
          <table className="w-full" data-id="element-1428">
            <thead data-id="element-1429">
              <tr className="border-b-2 border-gray-200" data-id="element-1430">
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1431">
                  Job Name
                </th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1432">
                  Customer
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1433">
                  Revenue
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1434">
                  Costs
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1435">
                  Profit
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1436">
                  Margin
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1437">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1438">
              {jobsWithProfit.map(job => <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1439">
                  <td className="py-3 px-2 text-sm font-medium text-gray-900" data-id="element-1440">
                    {job.name}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600" data-id="element-1441">
                    {job.customer}
                  </td>
                  <td className="py-3 px-2 text-sm text-right font-medium text-gray-900" data-id="element-1442">
                    {formatCurrency(job.revenue)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right text-red-600" data-id="element-1443">
                    {formatCurrency(job.totalCosts)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right font-semibold text-green-600" data-id="element-1444">
                    {formatCurrency(job.profit)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right" data-id="element-1445">
                    <span className={`inline-flex items-center gap-1 ${job.margin >= 50 ? 'text-green-600' : job.margin >= 30 ? 'text-blue-600' : 'text-orange-600'}`} data-id="element-1446">
                      {job.margin >= 50 ? <TrendingUpIcon className="w-3 h-3" data-id="element-1447" /> : job.margin < 30 ? <TrendingDownIcon className="w-3 h-3" data-id="element-1448" /> : null}
                      <span className="font-semibold" data-id="element-1449">
                        {job.margin.toFixed(1)}%
                      </span>
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center" data-id="element-1450">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded" data-id="element-1451">
                      {job.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-1452">
              <tr className="border-t-2 border-gray-300 bg-gray-50" data-id="element-1453">
                <td className="py-3 px-2 text-sm font-bold text-gray-900" colSpan={2} data-id="element-1454">
                  Totals
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-blue-600" data-id="element-1455">
                  {formatCurrency(totalRevenue)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-red-600" data-id="element-1456">
                  {formatCurrency(totalCosts)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-green-600" data-id="element-1457">
                  {formatCurrency(totalProfit)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-gray-900" data-id="element-1458">
                  {averageMargin.toFixed(1)}%
                </td>
                <td data-id="element-1459"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Cost Breakdown for Top Job */}
      <Card data-id="element-1460">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1461">
          Cost Breakdown: {mostProfitableJob.name}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1462">
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-1463">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1464">Labor Costs</p>
            <p className="text-xl font-bold text-blue-600" data-id="element-1465">
              {formatCurrency(mostProfitableJob.costs.labor)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1466">
              {(mostProfitableJob.costs.labor / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg" data-id="element-1467">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1468">Materials</p>
            <p className="text-xl font-bold text-orange-600" data-id="element-1469">
              {formatCurrency(mostProfitableJob.costs.materials)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1470">
              {(mostProfitableJob.costs.materials / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg" data-id="element-1471">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1472">Overhead</p>
            <p className="text-xl font-bold text-purple-600" data-id="element-1473">
              {formatCurrency(mostProfitableJob.costs.overhead)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1474">
              {(mostProfitableJob.costs.overhead / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
        </div>
      </Card>
    </div>;
}